import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import React from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  createRequestInfoInApolloOperation,
  addRequestInfoToErrorResponse,
} from '../../../hooks/errorHandlingRequestInfo';
import apolloCacheConfig from './apolloCacheConfig';
import { getApiBaseUrl } from '../../../utils/config';
import { useCurrentLocale } from '../../../context/I18n';
import { refreshAccessTokenSinglePromise } from '../../../api/jwtAuth/jwtAuthLogic';

const createRefreshJwtTokenLink = (logout: () => void) =>
  new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const handleRequest = (retryCount = 0) => {
        // infinity loop protection
        if (retryCount > 3) {
          logout();
          return;
        }
        forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (error) => {
            if (error.statusCode === 403) {
              // force logout if 403
              logout();
              return;
            }
            if (error.statusCode === 401) {
              refreshAccessTokenSinglePromise()
                .then((apiToken) => {
                  // Retry the request with the new token
                  if (apiToken) {
                    handleRequest(retryCount++);
                  } else {
                    // refresh token is expired -> logout
                    logout();
                  }
                })
                .catch((refreshError) => {
                  console.error(
                    'Error while refreshing token on retry',
                    refreshError,
                  );
                  logout();
                  return;
                });
            } else {
              observer.error(error);
            }
          },
          complete: () => observer.complete(),
        });
      };

      handleRequest();
    });
  });

const buildClient = (logout: () => void, locale: string) =>
  new ApolloClient({
    name: 'web',
    version: import.meta.env.VITE_APP_CODE_VERSION,
    connectToDevTools: true,
    link: ApolloLink.from([
      new ApolloLink((operation, forward) => {
        createRequestInfoInApolloOperation(operation);
        return forward(operation);
      }),
      onError((errorResponse) => {
        addRequestInfoToErrorResponse(errorResponse);
      }),
      createRefreshJwtTokenLink(logout),
      new HttpLink({
        uri: getApiBaseUrl() + '/internal/graphql',
        headers: {
          'flair-locale': locale,
        },
      }),
    ]),
    cache: new InMemoryCache(apolloCacheConfig),
  });

export const Provider: React.FC = ({ children }) => {
  const { isLoggedIn, logout } = useAuth();
  const locale = useCurrentLocale();
  if (isLoggedIn === false) {
    logout();
    return <></>;
  }

  const client = buildClient(logout, locale);

  return <ApolloProvider client={client} children={children} />;
};
